import React, { useMemo, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation, useParams } from "react-router";
import { Spin } from "antd";
import { useAuthTokenSSOClient } from "@companion-core/shared/app/Queries";
import { useAuthStore } from "@companion-core/shared/app/Store/authStore";
import { useBellStore } from "@companion-core/shared/app/Store/bellStore";
import { getHomeRoutes, getModule } from "@companion-core/shared/app/Utils/config";
import { reFetchAllResources } from "@companion-core/shared/app/Utils/apiClient";
import "Assets/Styles/scss/loader.scss";

interface AuthError {
  response?: {
    data?: {
      internal_error_code: number;
    };
  };
}

const SSOClient = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { pathname, state } = useLocation();
  const authCode = useMemo(() => searchParams.get("code") ?? params?.token, [params, searchParams]);
  const stateParams = useMemo(() => searchParams.get("state") ?? "", [searchParams]);
  const { setAuthenticated, setOnboardingHasAppeared } = useAuthStore((state) => ({
    setAuthenticated: state.setAuthenticated,
    setOnboardingHasAppeared: state.setOnboardingHasAppeared,
  }));
  const { setIsPopUpVisible } = useBellStore((state) => ({
    setIsPopUpVisible: state.setIsPopUpVisible,
  }));
  const redirectUri = useMemo(
    () => window.location.origin + window.location.pathname.replace(authCode ?? "", ""),
    [authCode]
  );
  const { error, isFetched, isSuccess } = useAuthTokenSSOClient(authCode ?? "", redirectUri);
  const { logged: homeRouteLogged } = getHomeRoutes() || "home";

  useEffect(() => {
    if (isFetched && authCode) {
      if (isSuccess) {
        if (getModule("concierge")?.modules?.bell?.options?.isPopUpVisibleByDefault) {
          setIsPopUpVisible(true);
        }
        searchParams.delete("code");
        let queryParams = "";
        searchParams.forEach((value, key) => {
          if (queryParams !== "") queryParams += "&";
          queryParams += `${key}=${value}`;
        });
        setAuthenticated(true);
        setOnboardingHasAppeared(false);
        if (stateParams && stateParams.startsWith("/")) {
          navigate({ pathname: stateParams });
        } else {
          navigate({
            pathname: params.token ? (state?.redirectPath ?? `/${homeRouteLogged}`) : pathname,
            search: `?${queryParams}`,
          });
        }

        reFetchAllResources();
      } else {
        setAuthenticated(false);
        navigate({
          pathname: "/sso/error",
          search: `?errorCode=${(error as AuthError)?.response?.data?.internal_error_code}`,
        });
        reFetchAllResources();
      }
    }
  }, [isFetched]);

  return (
    <div data-testid="sso" className="loading">
      <Spin data-testid="sso__loader" size="large" />
    </div>
  );
};

export default SSOClient;
